/**
 * MatchingQuestion.tsx
 * Professional drag-and-drop matching question with enhanced UX/UI
 */

import { Button } from '@vsat/shared/components';
import { cn } from '@vsat/shared/lib/utils';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { AnimatePresence, motion, useMotionValue } from 'framer-motion';
import { AlertCircle, Check, CheckCircle2, RotateCcw, Shuffle, X } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { parseFormulas } from './MathFormula';
import HtmlContent from './HtmlContent';

// Types
interface MatchItem {
  id: string;
  label: string;
  order?: number;
}

const shuffleList = (items: MatchItem[]): MatchItem[] => {
  const array = [...items];
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

const createOptionsSignature = (items: MatchItem[]) =>
  items.map(item => `${item.id}:${item.label}`).join('|');

interface MatchingQuestionProps {
  prompts: MatchItem[];
  options: MatchItem[];
  correct: Record<string, string>; // promptId → optionId
  questionNumber: string;
  value?: Record<string, string | null>;
  onChange?: (map: Record<string, string | null>) => void;
  showResult?: boolean;
  readOnly?: boolean;
  shuffledOrder?: string[]; // Stable shuffled order of option IDs (optional)
  onShuffledOrderChange?: (order: string[]) => void; // Callback to save shuffled order (optional)
}

// Enhanced draggable formula card with realistic animations
const DraggableOption = React.forwardRef<
  HTMLDivElement,
  {
    option: MatchItem;
    isUsed: boolean;
    readOnly?: boolean;
    onClick?: () => void;
    isGhost?: boolean;
    letterLabel?: string; // A, B, C, D, etc.
  }
>(({ option, isUsed, readOnly, onClick, isGhost = false, letterLabel }, forwardedRef) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: option.id,
    disabled: isUsed || readOnly,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <motion.div
      ref={node => {
        setNodeRef(node);
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      }}
      {...attributes}
      {...listeners}
      onClick={onClick}
      role='button'
      tabIndex={isUsed || readOnly ? -1 : 0}
      aria-label={`Formula option`}
      style={style}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{
        opacity: isGhost ? 0.3 : 1,
        scale: isDragging ? 1.05 : 1,
        rotate: isDragging ? 2 : 0,
        y: isDragging ? -8 : 0,
      }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={
        !isUsed && !readOnly && !isDragging
          ? {
              scale: 1.02,
              y: -2,
              transition: { type: 'spring', stiffness: 400, damping: 25 },
            }
          : {}
      }
      whileTap={
        !isUsed && !readOnly && !isDragging
          ? {
              scale: 0.98,
              transition: { type: 'spring', stiffness: 400, damping: 25 },
            }
          : {}
      }
      layout
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30,
      }}
      className={cn(
        'group relative px-4 py-3 rounded-lg border-2 select-none',
        'min-h-[56px] flex items-center justify-center',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        // Enhanced dragging styles
        isDragging && 'z-50 cursor-grabbing shadow-2xl shadow-primary/20',
        // Ghost placeholder styles
        isGhost && 'opacity-30 border-dashed border-border bg-gray-50/50 dark:bg-gray-800/50',
        isUsed &&
          !isGhost &&
          'opacity-30 cursor-not-allowed border-border bg-gray-50 dark:bg-gray-800 text-muted-foreground dark:text-gray-500',
        !isUsed &&
          !readOnly &&
          !isGhost &&
          'border-primary/40 bg-card text-foreground hover:border-primary dark:hover:border-primary hover:bg-primary/10 hover:shadow-lg cursor-grab active:cursor-grabbing transition-all duration-300',
        readOnly &&
          !isUsed &&
          !isGhost &&
          'border-border bg-gray-50 dark:bg-gray-800 text-muted-foreground'
      )}
    >
      <div className='flex items-center gap-3 w-full'>
        {letterLabel && (
          <div className='flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm bg-primary/10 text-primary border border-primary/20'>
            {letterLabel}
          </div>
        )}
        <div className='flex-1 text-sm font-medium text-center'>
          <HtmlContent content={option.label} />
        </div>
      </div>
    </motion.div>
  );
});

DraggableOption.displayName = 'DraggableOption';

// Enhanced drag overlay with floating animation
const FloatingDragOverlay = ({ activeOption }: { activeOption: MatchItem | null }) => {
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);

  const handleMouseMove = (event: React.MouseEvent) => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const deltaX = event.clientX - centerX;
    const deltaY = event.clientY - centerY;

    rotateX.set(deltaY / 10);
    rotateY.set(-deltaX / 10);
  };

  if (!activeOption) return null;

  return (
    <motion.div className='fixed inset-0 pointer-events-none z-50' onMouseMove={handleMouseMove}>
      <motion.div
        className='absolute'
        style={{
          rotateX,
          rotateY,
          transformStyle: 'preserve-3d',
        }}
        animate={{
          scale: [1.05, 1.1, 1.05],
          rotateZ: [0, 2, 0, -2, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <div className='relative'>
          {/* Drop shadow */}
          <div className='absolute inset-0 bg-primary/20 blur-xl scale-110 rounded-lg' />
          {/* Main card */}
          <div className='relative px-4 py-3 rounded-lg border-2 border-primary/40 dark:border-primary bg-card shadow-2xl shadow-primary/30 min-h-[56px] flex items-center justify-center'>
            <div className='text-sm font-medium text-center w-full text-foreground'>
              <HtmlContent content={activeOption.label} />
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
// Professional prompt card with drop zone
function PromptCard({
  promptId,
  index,
  prompt,
  assignedOption,
  showResult,
  correct,
  readOnly,
  onClick,
  onRemove,
  optionLabelMap,
}: {
  promptId: string;
  index: number;
  prompt: string;
  assignedOption?: MatchItem;
  showResult?: boolean;
  correct: Record<string, string>;
  readOnly?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  optionLabelMap?: Map<string, string>; // Map of option ID to letter label (A, B, C, D, etc.)
}) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({
    id: promptId,
    disabled: readOnly,
  });

  const isCorrect = showResult && assignedOption && correct[promptId] === assignedOption.id;
  const isIncorrect = showResult && assignedOption && correct[promptId] !== assignedOption.id;
  const isUnanswered = showResult && !assignedOption;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={cn(
        'group rounded-xl border p-4 shadow-sm hover:shadow-lg transition-all duration-300',
        isCorrect && 'bg-primary/10/50 dark:bg-primary/20/30 border-primary/40 dark:border-primary',
        isIncorrect && 'bg-rose-50/50 dark:bg-rose-900/30 border-rose-300 dark:border-rose-600',
        isUnanswered &&
          showResult &&
          'bg-amber-50/50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-600',
        !showResult && 'bg-card border-border',
        isOver && 'border-primary dark:border-primary bg-primary/10 scale-[1.01] shadow-lg'
      )}
    >
      {/* Prompt header with number */}
      <div className='flex items-start gap-3 mb-3'>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: index * 0.05 + 0.1, type: 'spring' }}
          className='flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-primary to-primary/80 text-white font-semibold text-sm flex items-center justify-center shadow-lg shadow-primary/25'
        >
          {index + 1}
        </motion.div>
        <div className='flex-1 text-sm font-medium text-foreground leading-relaxed pt-0.5'>
          <HtmlContent content={prompt} />
        </div>
      </div>

      {/* Enhanced drop zone with smooth animations */}
      <motion.div
        ref={setNodeRef}
        onClick={onClick}
        role='button'
        tabIndex={readOnly ? -1 : 0}
        aria-label={`Drop zone for prompt ${index + 1}`}
        className={cn(
          'min-h-[60px] px-4 py-3 rounded-lg border-2',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          'flex items-center justify-center relative overflow-hidden',
          !assignedOption &&
            !isOver &&
            'border-dashed border-border bg-gray-50/50 dark:bg-gray-800/50',
          isOver && 'border-primary dark:border-primary bg-primary/10',
          assignedOption && !showResult && 'border-border bg-gray-50 dark:bg-gray-800',
          isCorrect && 'border-primary dark:border-primary bg-primary/10/50 dark:bg-primary/20/30',
          isIncorrect && 'border-rose-400 dark:border-rose-600 bg-rose-50/50 dark:bg-rose-900/30',
          isUnanswered &&
            'border-amber-400 dark:border-amber-600 bg-amber-50/50 dark:bg-amber-900/30'
        )}
        animate={{
          scale: isOver ? 1.02 : 1,
        }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 25,
        }}
      >
        {/* Animated drop indicator */}
        {isOver && (
          <motion.div
            className='absolute inset-0 bg-primary/10 dark:bg-primary/20/20 rounded-lg'
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          />
        )}
        <div className='relative z-10 w-full'>
          {assignedOption ? (
            <motion.div
              className='w-full flex items-center justify-between gap-3'
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -10 }}
              transition={{
                type: 'spring',
                stiffness: 400,
                damping: 25,
                duration: 0.3,
              }}
            >
              <div className='flex items-center gap-3 flex-1'>
                {(() => {
                  // Get letter label for the assigned option
                  const letterLabel = optionLabelMap?.get(assignedOption.id) || '';
                  return letterLabel ? (
                    <div className='flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm bg-primary/10 text-primary border border-primary/20'>
                      {letterLabel}
                    </div>
                  ) : null;
                })()}
                <div
                  className={cn(
                    'flex-1 text-sm font-medium text-center',
                    !showResult && 'text-foreground',
                    isCorrect && 'text-primary dark:text-primary',
                    isIncorrect && 'text-rose-700 dark:text-rose-300'
                  )}
                >
                  <HtmlContent content={assignedOption.label} />
                </div>
              </div>
              {!readOnly && !showResult && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    onRemove?.();
                  }}
                  className='flex-shrink-0 w-6 h-6 rounded-full hover:bg-gray-200 dark:hover.bg-gray-700 text-muted-foreground dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 flex items-center justify-center transition-all duration-300'
                  aria-label={t('landing.questions.matching.removeFormula', 'Remove formula')}
                >
                  <X className='w-4 h-4' />
                </button>
              )}
              {showResult && isCorrect && <Check className='w-5 h-5 text-primary flex-shrink-0' />}
              {showResult && isIncorrect && (
                <X className='w-5 h-5 text-rose-600 dark:text-rose-400 flex-shrink-0' />
              )}
              {showResult && isUnanswered && (
                <AlertCircle className='w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0' />
              )}
            </motion.div>
          ) : (
            <motion.span
              className='text-sm text-muted-foreground dark:text-gray-500 font-medium'
              animate={{
                opacity: isOver ? 1 : 0.7,
                scale: isOver ? 1.05 : 1,
              }}
              transition={{ duration: 0.2 }}
            >
              {isOver
                ? t('landing.questions.matching.dropZone.hover', 'Release to drop')
                : t('landing.questions.matching.dropZone.empty', 'Drop formula here')}
            </motion.span>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
// Main component
export function MatchingQuestion({
  prompts,
  options,
  correct,
  questionNumber,
  value = {},
  onChange,
  showResult = false,
  readOnly = false,
  shuffledOrder,
  onShuffledOrderChange,
}: MatchingQuestionProps) {
  const { t } = useTranslation();
  const [assignments, setAssignments] = React.useState<Record<string, string | null>>(value);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [selectedOption, setSelectedOption] = React.useState<string | null>(null);

  // Use stable shuffled order if provided, otherwise shuffle once
  const getInitialShuffledOptions = React.useCallback(() => {
    if (shuffledOrder && shuffledOrder.length === options.length) {
      // Restore order from prop
      const orderMap = new Map(shuffledOrder.map((id, idx) => [id, idx]));
      return [...options].sort((a, b) => {
        const idxA = orderMap.get(a.id) ?? Infinity;
        const idxB = orderMap.get(b.id) ?? Infinity;
        return idxA - idxB;
      });
    }
    // Shuffle once on first mount
    return shuffleList(options);
  }, [options, shuffledOrder]);

  const [shuffledOptions, setShuffledOptions] = React.useState(getInitialShuffledOptions);
  const [forceRender, setForceRender] = React.useState(0);
  const optionsSignatureRef = React.useRef(createOptionsSignature(options));
  const promptsSignatureRef = React.useRef(createOptionsSignature(prompts));
  const shuffledOrderRef = React.useRef<string[] | undefined>(shuffledOrder);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  React.useEffect(() => {
    setAssignments(value);
  }, [value]);

  // Reset state when prompts or options change (new question)
  // Also update when shuffledOrder prop changes (navigating back to previous question)
  // Create signatures for both prompts and options to detect question changes
  React.useEffect(() => {
    const promptsSignature = createOptionsSignature(prompts);
    const optionsSignature = createOptionsSignature(options);

    // If prompts or options changed, reset all state for new question
    const promptsChanged = promptsSignature !== promptsSignatureRef.current;
    const optionsChanged = optionsSignature !== optionsSignatureRef.current;

    // Check if shuffledOrder prop changed (user navigated back to a question with stored order)
    const hasStoredOrder = shuffledOrder && shuffledOrder.length === options.length;
    const shuffledOrderChanged =
      shuffledOrderRef.current !== shuffledOrder &&
      shuffledOrderRef.current?.join('|') !== shuffledOrder?.join('|');

    if (promptsChanged || optionsChanged) {
      // Update refs
      promptsSignatureRef.current = promptsSignature;
      optionsSignatureRef.current = optionsSignature;
      shuffledOrderRef.current = shuffledOrder;

      // Reset all state for new question
      setAssignments(value || {});
      setActiveId(null);
      setSelectedOption(null);

      // Use stable shuffled order if provided, otherwise shuffle once
      const newShuffledOptions = hasStoredOrder
        ? (() => {
            // Restore order from prop (maintains consistent order across navigation)
            const orderMap = new Map(shuffledOrder.map((id, idx) => [id, idx]));
            return [...options].sort((a, b) => {
              const idxA = orderMap.get(a.id) ?? Infinity;
              const idxB = orderMap.get(b.id) ?? Infinity;
              return idxA - idxB;
            });
          })()
        : shuffleList(options); // Shuffle once on first visit to this question

      setShuffledOptions(newShuffledOptions);

      // Save shuffled order to parent if callback provided and order is new (not from prop)
      if (onShuffledOrderChange && !hasStoredOrder) {
        onShuffledOrderChange(newShuffledOptions.map(opt => opt.id));
      }

      setForceRender(prev => prev + 1);
    } else if (shuffledOrderChanged && hasStoredOrder) {
      // Question didn't change but shuffledOrder prop changed (navigated back)
      // Restore the stored shuffled order to maintain consistency
      shuffledOrderRef.current = shuffledOrder;
      const orderMap = new Map(shuffledOrder.map((id, idx) => [id, idx]));
      const restoredOptions = [...options].sort((a, b) => {
        const idxA = orderMap.get(a.id) ?? Infinity;
        const idxB = orderMap.get(b.id) ?? Infinity;
        return idxA - idxB;
      });
      setShuffledOptions(restoredOptions);
    }
  }, [prompts, options, value, shuffledOrder, onShuffledOrderChange]);

  // Memoize usedOptions to ensure it updates properly with state changes
  const usedOptions = React.useMemo(() => {
    return new Set(Object.values(assignments).filter(Boolean) as string[]);
  }, [assignments]);

  const getOptionById = (id: string) => options.find(opt => opt.id === id);

  // Ref for auto-scrolling the right column
  const rightColumnRef = React.useRef<HTMLDivElement>(null);

  const updateAssignments = React.useCallback(
    (newAssignments: Record<string, string | null>) => {
      setAssignments(newAssignments);
      onChange?.(newAssignments);

      // Force a re-render to ensure the UI updates properly
      setForceRender(prev => prev + 1);

      // Auto-scroll to top of right column after assignment changes
      setTimeout(() => {
        if (rightColumnRef.current) {
          rightColumnRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    },
    [onChange]
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const optionId = active.id as string;
    const promptId = over.id as string;

    // Create a completely new assignments object
    const newAssignments = { ...assignments };

    // Find if this option is already assigned to another prompt
    const previousPromptId = Object.entries(assignments).find(
      ([, optId]) => optId === optionId
    )?.[0];

    // Find what option is currently assigned to the target prompt (if any)
    const currentOptionInTarget = assignments[promptId];

    // If the dragged option is the same as what's already in the target, do nothing
    if (currentOptionInTarget === optionId) {
      return;
    }

    // Assign the dragged option to the target prompt
    newAssignments[promptId] = optionId;

    // If the dragged option was previously assigned to another prompt, clear that assignment
    if (previousPromptId && previousPromptId !== promptId) {
      newAssignments[previousPromptId] = null;
    }

    // If there was a different option in the target prompt, it will automatically
    // become available again since we're overwriting its assignment

    updateAssignments(newAssignments);
  };

  const handleTapOption = (optionId: string) => {
    if (readOnly) return;
    setSelectedOption(selectedOption === optionId ? null : optionId);
  };

  const handleTapSlot = (promptId: string) => {
    if (readOnly || !selectedOption) return;

    const newAssignments = { ...assignments };

    // Find if the selected option is already assigned to another prompt
    const previousPromptId = Object.entries(assignments).find(
      ([, optId]) => optId === selectedOption
    )?.[0];

    // Find what option is currently assigned to the target prompt (if any)
    const currentOptionInTarget = assignments[promptId];

    // If the selected option is the same as what's already in the target, do nothing
    if (currentOptionInTarget === selectedOption) {
      setSelectedOption(null);
      return;
    }

    // Assign the selected option to the target prompt
    newAssignments[promptId] = selectedOption;

    // If the selected option was previously assigned to another prompt, clear that assignment
    if (previousPromptId && previousPromptId !== promptId) {
      newAssignments[previousPromptId] = null;
    }

    // If there was a different option in the target prompt, it will automatically
    // become available again since we're overwriting its assignment

    updateAssignments(newAssignments);
    setSelectedOption(null);
  };

  const handleRemove = (promptId: string) => {
    const newAssignments = { ...assignments };
    newAssignments[promptId] = null;
    updateAssignments(newAssignments);
  };

  const handleShuffle = () => {
    const newShuffled = shuffleList(shuffledOptions);
    setShuffledOptions(newShuffled);
    // Update parent with new shuffled order
    if (onShuffledOrderChange) {
      onShuffledOrderChange(newShuffled.map(opt => opt.id));
    }
  };

  const handleReset = () => {
    const resetAssignments = prompts.reduce(
      (acc, p) => {
        acc[p.id] = null;
        return acc;
      },
      {} as Record<string, string | null>
    );
    updateAssignments(resetAssignments);
    setSelectedOption(null);
  };

  const activeOption = activeId ? getOptionById(activeId) : null;

  const allAssigned = prompts.every(p => assignments[p.id]);
  const availableCount = shuffledOptions.filter(option => !usedOptions.has(option.id)).length;

  // Create a stable map of option ID to letter label (A, B, C, D, etc.)
  // Based on the shuffled order, so labels remain consistent
  const optionLabelMap = React.useMemo(() => {
    const map = new Map<string, string>();
    shuffledOptions.forEach((option, index) => {
      map.set(option.id, String.fromCharCode(65 + index)); // A, B, C, D, ...
    });
    return map;
  }, [shuffledOptions]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <DragOverlay>
        {activeOption ? (
          <motion.div
            className='px-4 py-3 rounded-lg border-2 border-primary/40 dark:border-primary bg-card shadow-2xl shadow-primary/30 min-h-[56px] flex items-center justify-center cursor-grabbing'
            animate={{
              scale: 1.05,
              rotate: 2,
              y: -8,
            }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30,
            }}
          >
            <div className='flex items-center gap-3 w-full'>
              {(() => {
                // Get stable letter label from the map
                const letterLabel = optionLabelMap?.get(activeOption.id) || '';
                return letterLabel ? (
                  <div className='flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm bg-primary/10 text-primary border border-primary/20'>
                    {letterLabel}
                  </div>
                ) : null;
              })()}
              <div className='flex-1 text-sm font-medium text-center text-foreground'>
                <HtmlContent content={activeOption.label} />
              </div>
            </div>
          </motion.div>
        ) : null}
      </DragOverlay>
      {/* <div className='mb-4'>
        <div className='flex items-center justify-between'>
          <h3 className='text-sm font-bold text-foreground'>
            {parseFormulas(questionNumber)}
          </h3>
          <div className='text-xs text-muted-foreground'>
            <Badge
              variant='secondary'
              className='text-sm px-3 py-1 font-mono bg-primary/10 text-primary border-primary/40'
            >
              {prompts.filter(p => assignments[p.id]).length} / {prompts.length}
            </Badge>
            <div className='h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full flex-1 max-w-[200px] overflow-hidden'>
              <motion.div
                initial={{ width: 0 }}
                animate={{
                  width: `${(prompts.filter(p => assignments[p.id]).length / prompts.length) * 100}%`,
                }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className='h-1.5 bg-gradient-to-r from-primary to-primary/80 rounded-full'
              />
            </div>
          </div>
        </div>
      </div> */}
      <div className='grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-6 h-full'>
        {/* Left: Prompts */}
        <div data-tour='answers' className='space-y-4 overflow-y-auto max-h-[600px]'>
          <div className='space-y-4'>
            {prompts.map((prompt, idx) => (
              <PromptCard
                key={prompt.id}
                promptId={prompt.id}
                index={idx}
                prompt={prompt.label}
                assignedOption={
                  assignments[prompt.id]
                    ? getOptionById(assignments[prompt.id] as string)
                    : undefined
                }
                showResult={showResult}
                correct={correct}
                readOnly={readOnly}
                onClick={() => handleTapSlot(prompt.id)}
                onRemove={() => handleRemove(prompt.id)}
                optionLabelMap={optionLabelMap}
              />
            ))}
          </div>
        </div>

        {/* Right: Formula Bank */}
        <div
          ref={rightColumnRef}
          data-tour='answers-bank'
          className='space-y-4 overflow-y-auto max-h-[600px]'
        >
          <div className='sticky top-4 h-full'>
            <div className='bg-card rounded-xl border border-border p-5 shadow-sm h-full group hover:shadow-lg transition-all duration-300 hover:ring-2 hover:ring-primary/20'>
              <div className='flex items-center justify-between mb-4'>
                <div>
                  <h3 className='text-base font-semibold text-foreground'>
                    {t('landing.questions.matching.formulaBank', 'Formula Bank')}
                  </h3>
                  <p className='text-sm text-muted-foreground mt-1'>
                    {availableCount === 1
                      ? t('landing.questions.matching.formulaAvailable', {
                          count: availableCount,
                          defaultValue: `${availableCount} formula available`,
                        })
                      : t('landing.questions.matching.formulasAvailable', {
                          count: availableCount,
                          defaultValue: `${availableCount} formulas available`,
                        })}
                  </p>
                </div>
                {!readOnly && (
                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={handleShuffle}
                      className='h-8 w-8 p-0 border-border hover:bg-primary/10 hover:border-primary/30 transition-all duration-300'
                      title={t('landing.questions.matching.shuffleFormulas', 'Shuffle formulas')}
                    >
                      <Shuffle className='h-3.5 w-3.5' />
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={handleReset}
                      className='h-8 w-8 p-0 border-border hover:bg-primary/10 hover:border-primary/30 transition-all duration-300'
                      title={t('landing.questions.matching.resetAll', 'Reset all')}
                    >
                      <RotateCcw className='h-3.5 w-3.5' />
                    </Button>
                  </div>
                )}
              </div>

              <div key={forceRender} className='space-y-3 min-h-[200px]'>
                <AnimatePresence mode='popLayout'>
                  {shuffledOptions
                    .filter(option => !usedOptions.has(option.id))
                    .map(option => {
                      const isDragging = activeId === option.id;
                      // Get stable letter label from the map
                      const letterLabel = optionLabelMap.get(option.id) || '';
                      return (
                        <React.Fragment key={option.id}>
                          {/* Original draggable option */}
                          <DraggableOption
                            option={option}
                            isUsed={false}
                            readOnly={readOnly}
                            onClick={() => handleTapOption(option.id)}
                            letterLabel={letterLabel}
                          />
                          {/* Ghost placeholder when dragging */}
                          {isDragging && (
                            <DraggableOption
                              option={option}
                              isUsed={false}
                              readOnly={readOnly}
                              isGhost={true}
                              letterLabel={letterLabel}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                </AnimatePresence>

                {availableCount === 0 && allAssigned && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ type: 'spring', bounce: 0.4 }}
                    className='flex flex-col items-center justify-center py-12 text-center'
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.1, type: 'spring', bounce: 0.5 }}
                      className='w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-3 border border-primary/40'
                    >
                      <CheckCircle2 className='w-8 h-8 text-primary' />
                    </motion.div>
                    <p className='text-sm font-medium text-foreground'>
                      {t('landing.questions.matching.allAssigned', 'All formulas assigned!')}
                    </p>
                    <p className='text-xs text-muted-foreground mt-1'>
                      {t('landing.questions.matching.reviewAnswers', 'Review your answers above')}
                    </p>
                  </motion.div>
                )}

                {availableCount === 0 && !allAssigned && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className='flex flex-col items-center justify-center py-12 text-center'
                  >
                    <div className='w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-3 border border-amber-200 dark:border-amber-800'>
                      <AlertCircle className='w-8 h-8 text-amber-600 dark:text-amber-400' />
                    </div>
                    <p className='text-sm font-medium text-foreground'>
                      {t('landing.questions.matching.noFormulas', 'No formulas available')}
                    </p>
                    <p className='text-xs text-muted-foreground mt-1'>
                      {t('landing.questions.matching.allUsed', 'All have been used')}
                    </p>
                  </motion.div>
                )}
              </div>

              <AnimatePresence>
                {selectedOption && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className='mt-4 p-3 rounded-lg bg-primary/10 border border-primary/40'
                  >
                    <p className='text-sm font-medium text-primary/20 dark:text-primary'>
                      {t('landing.questions.matching.selected', 'Selected:')}{' '}
                      {parseFormulas(getOptionById(selectedOption)?.label || '')}
                    </p>
                    <p className='text-xs text-primary mt-1'>
                      {t('landing.questions.matching.clickToAssign', 'Click a drop zone to assign')}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </DndContext>
  );
}
